/**
 * Who signs, in what order, and what has to be filled in first.
 *
 * ## The shape this replaces
 *
 * A customer contract used to be signed by the company *implicitly*, at the
 * moment it was sent: `contractSignature.create({ party: 'company' })` ran
 * inside the send transaction, naming whichever signatory the settings held.
 * Nobody from the company ever looked at the document and agreed to it — the
 * act of emailing it was treated as the act of signing it.
 *
 * That is defensible for a standard package at a standard price, and it stops
 * being defensible the moment a contract carries anything negotiated. It is
 * also not what anybody means by "signed": if it were ever questioned, the
 * honest answer would be that a dispatcher pressed Send.
 *
 * So signing is now a sequence of turns. Somebody with authority signs on the
 * company's behalf, and only then is the other party invited. The document is
 * locked between turns, because a document that can change after one party has
 * signed it has not been signed by them at all.
 *
 * ## Why the order is company-first rather than either-way
 *
 * An offer has to exist before it can be accepted. A contract the employee has
 * signed and the company has not is not a contract — it is an application, and
 * treating it as the former is how somebody turns up for work on Monday to a
 * job nobody approved.
 *
 * It also puts the reviewing burden in the right place. The company sees the
 * filled-in document, checks the salary and the start date, and signs; the
 * employee then sees exactly what was signed, with nothing left blank for
 * somebody to complete afterwards.
 */
/* ── Who signs ────────────────────────────────────────────────────────────── */
export const SIGNING_PARTIES = ['company', 'counterparty'];
/**
 * What to call the other side on a screen.
 *
 * A lookup rather than a chain of ternaries. The chain was fine with two
 * audiences and became wrong the moment a third arrived: adding `landlord` to
 * `Counterparty` left `counterparty === 'employee' ? … : 'the customer'`
 * compiling happily and calling every landlord a customer. A record keyed by
 * the union is a compile error when a case is missed, which is the point.
 */
export const COUNTERPARTY_LABEL = {
    customer: 'the customer',
    employee: 'the employee',
    landlord: 'the landlord',
};
/**
 * What to call each document in a sentence.
 *
 * Used in the emails, where "your service agreement" and "your letter of
 * appointment" are the difference between a message that reads as written for
 * somebody and one that reads as generated. A record rather than a ternary for
 * the reason given above: a missing case must be a compile error.
 */
export const DOCUMENT_NAME = {
    customer: 'service agreement',
    employee: 'letter of appointment',
    landlord: 'building access agreement',
};
/** Who the other side should chase, when it is not their turn yet. */
export const WHO_SENDS_IT_ON = {
    customer: 'The office',
    employee: 'HR',
    landlord: 'The office',
};
/* ── What is being signed ─────────────────────────────────────────────────── */
export const ENGAGEMENT_KINDS = ['employment', 'attachment', 'internship'];
/**
 * How long each kind of engagement runs.
 *
 * These are the business's own terms rather than anything statutory, and they
 * are here so the end date cannot be typed differently on two contracts for
 * the same kind of role. Changing one changes every contract raised afterwards
 * and none already signed.
 */
export const ENGAGEMENTS = {
    employment: {
        kind: 'employment',
        label: 'Employment',
        months: 12,
        note: 'One year, reviewed and renewed on the evaluation at the end of the probation period.',
    },
    attachment: {
        kind: 'attachment',
        label: 'Attachment',
        months: 3,
        note: 'Three months. Attachment is a placement, not a post.',
    },
    internship: {
        kind: 'internship',
        label: 'Internship',
        months: 6,
        note: 'Six months.',
    },
};
export function isEngagementKind(value) {
    return ENGAGEMENT_KINDS.includes(value);
}
/**
 * When an engagement ends, given when it starts.
 *
 * Calendar months rather than a day count, so a contract starting on the 2nd
 * of June ends on the 2nd of June — which is what both parties will read it as,
 * whatever the intervening months happen to contain.
 *
 * The end-of-month case is handled explicitly: three months from the 31st of
 * January is the 30th of April, not the 1st of May. `setUTCMonth` would roll
 * over silently and produce a contract a day longer than the one anybody
 * agreed, every time it happened.
 */
export function engagementEnds(start, kind) {
    const months = ENGAGEMENTS[kind].months;
    const day = start.getUTCDate();
    const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + months, 1));
    const lastOfThatMonth = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0)).getUTCDate();
    end.setUTCDate(Math.min(day, lastOfThatMonth));
    return end;
}
/**
 * Whether every field belonging to a party has been filled.
 *
 * The SignEasy property, and the reason signing is not a single checkbox: a
 * document with an empty National ID box has not been completed, and finding
 * that out weeks later means asking somebody to sign again. So the turn cannot
 * be finished while anything of theirs is blank.
 *
 * Only *their* fields. The company cannot be blocked by an employee's ID
 * number, and the employee cannot be asked for the company's registration.
 */
export function unfilledFields(fields, values, party) {
    return fields.filter((field) => {
        if (field.party !== party)
            return false;
        if (!field.required)
            return false;
        const value = values[field.key];
        if (value === undefined || value === null)
            return true;
        return String(value).trim().length === 0;
    });
}
/** The sentence to show when a turn cannot be finished, or null. */
export function refuseIncomplete(fields, values, party) {
    const missing = unfilledFields(fields, values, party);
    if (missing.length === 0)
        return null;
    const names = missing.map((field) => field.label);
    const list = names.length === 1
        ? names[0]
        : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    return missing.length === 1
        ? `${list} is still empty. Every box has to be filled before you can sign.`
        : `${list} are still empty. Every box has to be filled before you can sign.`;
}
/**
 * Whose turn it is, from who has signed already.
 *
 * Derived rather than stored. A stored "current turn" is a second source of
 * truth about the same fact, and the two drift the first time a signature is
 * written by a path that forgets to advance it — at which point a contract is
 * either stuck or signable twice.
 */
export function signingState(signed) {
    const has = (party) => signed.includes(party);
    if (!has('company'))
        return { turn: 'company', signed, complete: false };
    if (!has('counterparty'))
        return { turn: 'counterparty', signed, complete: false };
    return { turn: null, signed, complete: true };
}
/**
 * Why this party cannot sign right now, or null if they can.
 *
 * The order is enforced here rather than trusted to the screen. A screen that
 * hides the button is a courtesy; this is the rule, and it is what stops a
 * signing link being used out of turn by somebody who kept the email.
 */
export function refuseSignature(party, signed, counterparty = 'customer') {
    if (signed.includes(party)) {
        return party === 'company'
            ? 'The company has already signed this.'
            : 'You have already signed this.';
    }
    const state = signingState(signed);
    if (state.turn !== party) {
        if (party === 'counterparty') {
            return `This is not ready for you yet — nobody has signed it for the company. ${WHO_SENDS_IT_ON[counterparty]} will send it on once they have.`;
        }
        return 'That party cannot sign at this point.';
    }
    return null;
}
/** Who signs next, phrased for a screen. Null when it is finished. */
export function nextSignee(signed, counterparty = 'customer') {
    const state = signingState(signed);
    if (!state.turn)
        return null;
    return {
        party: state.turn,
        label: state.turn === 'company' ? 'the company' : COUNTERPARTY_LABEL[counterparty],
    };
}
/* ── The employment contract's own fields ─────────────────────────────────── */
/**
 * What an employment offer needs, split by who fills it.
 *
 * Taken from the letter this business actually sends. The company's half is
 * everything it is offering — position, salary, start date, who to report to —
 * because those are the terms being offered and the employee cannot be asked
 * to supply them. The employee's half is the identity and the signature.
 *
 * The KRA PIN and National ID sit on the employee's side deliberately: they
 * are the employee's own numbers, HR frequently does not have them at the
 * point of drafting, and a box HR guesses at is a box that goes in wrong.
 */
export function employmentFields(kind) {
    const engagement = ENGAGEMENTS[kind];
    return [
        /* ── The company's half ───────────────────────────────────────────── */
        {
            key: 'position',
            label: 'Position',
            kind: 'text',
            party: 'company',
            required: true,
            hint: 'As it should read on the letter, for example "Site technician".',
        },
        {
            key: 'reportsTo',
            label: 'Reports to',
            kind: 'text',
            party: 'company',
            required: true,
            hint: 'The person and their role.',
        },
        {
            key: 'basedAt',
            label: 'Based at',
            kind: 'text',
            party: 'company',
            required: true,
        },
        {
            key: 'startDate',
            label: 'Start date',
            kind: 'date',
            party: 'company',
            required: true,
            hint: `${engagement.label} runs ${engagement.months} months from this date.`,
        },
        {
            key: 'grossMonthlyKes',
            label: 'Gross monthly salary (KES)',
            kind: 'money',
            party: 'company',
            required: true,
        },
        {
            key: 'payDayOfMonth',
            label: 'Paid on (day of month)',
            kind: 'text',
            party: 'company',
            required: true,
        },
        /*
         * The per-job rates, on the contract rather than in somebody's head.
         *
         * Only on an employment engagement: an attachment or an internship is not
         * paid piecework, and asking for three rates that will never be used is
         * three more boxes between a person and a signature.
         *
         * They are fields rather than fixed text because a rate that cannot be
         * changed without a code deploy is a rate that gets paid outside the
         * contract instead — which is the situation this replaces.
         */
        ...(kind === 'employment'
            ? [
                {
                    key: 'ratePerConnectionKes',
                    label: 'Allowance per new connection (KES)',
                    kind: 'money',
                    party: 'company',
                    required: true,
                    hint: 'Paid for each new customer connection completed and verified.',
                },
                {
                    key: 'connectionsPerDayCap',
                    label: 'Connections paid per day (limit)',
                    kind: 'text',
                    party: 'company',
                    required: true,
                    hint: 'The most connections in one day that attract the allowance.',
                },
                {
                    key: 'ratePerKilometreKes',
                    label: 'Allowance per kilometre of pole cable run (KES)',
                    kind: 'money',
                    party: 'company',
                    required: true,
                },
                {
                    key: 'ratePerFatKes',
                    label: 'Allowance per successful FAT configuration (KES)',
                    kind: 'money',
                    party: 'company',
                    required: true,
                },
            ]
            : []),
        {
            key: 'companySignatoryName',
            label: 'Signing for the company — name',
            kind: 'text',
            party: 'company',
            required: true,
        },
        {
            key: 'companySignatoryTitle',
            label: 'Signing for the company — title',
            kind: 'text',
            party: 'company',
            required: true,
            hint: 'For example CEO.',
        },
        {
            key: 'companySignature',
            label: 'Company signature',
            kind: 'signature',
            party: 'company',
            required: true,
        },
        /* ── The employee's half ──────────────────────────────────────────── */
        {
            key: 'employeeName',
            label: 'Your full name',
            kind: 'text',
            party: 'counterparty',
            required: true,
            hint: 'As it appears on your ID.',
        },
        {
            key: 'nationalId',
            label: 'National ID number',
            kind: 'text',
            party: 'counterparty',
            required: true,
        },
        {
            key: 'kraPin',
            label: 'KRA PIN',
            kind: 'text',
            party: 'counterparty',
            required: true,
            hint: 'On your KRA certificate. Needed for PAYE.',
        },
        {
            key: 'mobile',
            label: 'Mobile number',
            kind: 'text',
            party: 'counterparty',
            required: true,
        },
        {
            key: 'employeeEmail',
            label: 'Email',
            kind: 'text',
            party: 'counterparty',
            required: true,
        },
        {
            key: 'employeeSignature',
            label: 'Your signature',
            kind: 'signature',
            party: 'counterparty',
            required: true,
        },
        /*
         * The declaration of secrecy is a second signature on the same document,
         * and the letter treats it as separate — its own heading, its own signature
         * line. Keeping it as its own field means "I signed the contract" and "I
         * signed the declaration" remain two answerable questions.
         */
        {
            key: 'secrecySignature',
            label: 'Declaration of secrecy — signature',
            kind: 'signature',
            party: 'counterparty',
            required: true,
            hint: 'The declaration at the end of the letter is signed separately.',
        },
    ];
}
//# sourceMappingURL=signing.js.map